/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { Modal } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth";
import clsx from "clsx";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import { ingredientSchema } from "../core/Schema";
import { Ingredient, initialValues } from "../core/_models";
import { findDuplicateIngredient, updateIngredient, viewIngredientById } from "../core/_requests";
import CloseButton from "../../../component/core/Buttons";

interface ModalProps {
  show: boolean;
  onHide: () => void;
  id: string;
}

const EditIngredient: React.FC<ModalProps> = ({ show, onHide, id }) => {
  const { currentUser } = useAuth();
  const [data, setData] = useState<Ingredient>(initialValues);
  const [duplicate, setDuplicate] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  // ✅ Fetch ingredient details when modal opens
  useEffect(() => {
    if (id && show) {
      viewIngredientById(id)
        .then((res) => {
          if (res?.status && res?.data) {
            const ing = res.data;
            setData({
              primaryId: ing.primaryId || "",
              name: ing.name || "",
              chefId: ing.chefId || "",
              createdBy: ing.createdBy || "",
              updatedBy: ing.updatedBy || "",
            });
          }
        })
        .catch(() => {
          setErrorMessage("Unable to load ingredient details");
        });
    }
  }, [id, show]);

  // ✅ Formik setup
  const formik = useFormik<Ingredient>({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: ingredientSchema,
    onSubmit: async (values, { resetForm }) => {
  const updatedData = {
    ...data,
    ...values,
    chefId: currentUser?.uuid,
    createdBy: currentUser?.chefId,
    updatedBy: currentUser?.chefId ?? null, // ✅ ensure integer or null
  };

  setLoading(true);
  updateIngredient(id, updatedData)

        .then((res) => {
          if (res?.status) {
            toast.success(res?.message || "Ingredient updated successfully!");
            resetForm();
            setData(initialValues);
            onHide();
          } else {
            toast.error(res?.message || "Failed to update ingredient.");
          }
        })
        .catch(() => setErrorMessage("Something went wrong"))
        .finally(() => setLoading(false));
    },
  });

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Ingredient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            ref={formRef}
            onSubmit={formik.handleSubmit}
            noValidate
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          >
            <div className="col-12 mb-3">
              <label className="form-label require">Ingredient</label>
              <input
                type="text"
                className={clsx("form-control", {
                  "is-invalid": formik.touched.name && formik.errors.name,
                  "is-valid": formik.touched.name && !formik.errors.name,
                })}
                placeholder="Enter Ingredient Name"
                {...formik.getFieldProps("name")}
                onChange={(e) => {
                  formik.handleChange(e);
                  findDuplicateIngredient(e.target.value).then((response) => {
                    if (response?.isDuplicate) {
                      setDuplicate("Ingredient name already exists");
                    } else {
                      setDuplicate("");
                    }
                  });
                }}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.name}</span>
                </div>
              )}
              {duplicate && <span style={{ color: "red" }}>{duplicate}</span>}
            </div>

            <CloseButton
              onClose={onHide}
              onSubmit={formik.handleSubmit}
              loading={loading}
              disabled={formik.isSubmitting || !formik.isValid || duplicate !== ""}
            />
          </form>
        </Modal.Body>
      </Modal>
      <ToastContainer />
    </>
  );
};

export default EditIngredient;
